/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/scandipwa
 */

import { CartQuery as SourceCartQuery } from 'SourceQuery/Cart.query';
import { Field } from 'Util/Query';

/** @namespace Pwa/Query/Cart/Query */
export class CartQuery extends SourceCartQuery {
    _getShippingTimeFields() {
        return [
            'time',
            'status'
        ];
    }

    _getShippingTimeField() {
        return new Field('shippingtime')
            .addFieldList(this._getShippingTimeFields());
    }

    _getFeePaymentFields() {
        return [
            'title',
            'value',
            'currency'
        ];
    }

    __getFeePaymentField() {
        return new Field('fee_payment')
            .addFieldList(this._getFeePaymentFields());
    }

    _getCartItemAttachments() {
        return new Field('attachments')
            .addFieldList(['attachmentItemId', 'markingCode', 'fileName', 'fileUrl', 'fileSize']);
    }

    _getCartItemConfiguration() {
        return new Field('product_configuration');
    }

    _getCartItemSelectedFiller() {
        return new Field('selected_filler');
    }

    _getBoxFillerField() {
        return new Field('boxfillers')
            .addFieldList(['sku', 'name', 'color', 'thumbnail', this._getBoxFillerPriceField()]);
    }

    _getBoxFillerPriceField() {
        return new Field('price')
            .addFieldList(['value', 'currency']);
    }

    _getBoxFillerMessageOutput() {
        return new Field('filler_message')
            .addFieldList(['status', 'message']);
    }

    _getCartItemFields() {
        return [
            'qty',
            'sku',
            'price',
            'price_incl_tax',
            'item_id',
            'row_total',
            'row_total_incl_tax',
            'tax_amount',
            'tax_percent',
            'discount_amount',
            'discount_percent',
            this._getCustomizableOptionsFields(),
            this._getDownloadableLinksField(),
            this._getBundleOptionsField(),
            this._getProductField(),
            this._getCartItemAttachments(),
            this._getCartItemConfiguration(),
            this._getCartItemSelectedFiller(),
            this._getBoxFillerMessageOutput()
        ];
    }

    _getCartTotalsFields() {
        return [
            'id',
            'subtotal',
            'subtotal_incl_tax',
            'items_qty',
            'tax_amount',
            'grand_total',
            'marking_value',
            'discount_amount',
            'quote_currency_code',
            'subtotal_with_discount',
            'coupon_code',
            'shipping_amount',
            'shipping_incl_tax',
            'shipping_tax_amount',
            'is_virtual',
            'applied_rule_ids',
            'shipping_amount',
            'shipping_incl_tax',
            'shipping_tax_amount',
            'shipping_method',
            'is_in_store_pickup_available',
            'free_delivery',
            'prepayment_discount',
            'selected_shipping_address_id',
            this._getBoxFillerField(),
            this.__getFeePaymentField(),
            this._getShippingTimeField(),
            this._getCartItemsField(),
            this._getAppliedTaxesField()
        ];
    }
}

export default new CartQuery();
